import { CAMERA, INPUT } from '../config/tuning';
import { clamp } from '../flight/Aerodynamics';
import { type Bindings, InputAction, type InputActionId } from './Bindings';

/**
 * Keyboard and mouse (SPEC §3.13).
 *
 * The mouse uses a **virtual joystick**: movement deflects a simulated stick that recentres at a
 * configurable rate, and the stick — not the cursor — commands the aircraft. Direct 1:1
 * mouse-to-surface mapping is unflyable and is explicitly not used.
 *
 * Keyboard axes ramp toward full deflection rather than stepping to it, because a digital key
 * driving a rate command is otherwise a switch between "nothing" and "maximum".
 *
 * Event handling is separated from state via the public `handleKeyDown`/`handleKeyUp`/
 * `handleMouseMove` methods, so the whole device can be driven from a test with no DOM.
 */

export interface AxisSample {
  /** −1..1, positive rolls right. */
  roll: number;
  /** −1..1, positive pitches nose up. */
  pitch: number;
  /** −1..1, positive yaws nose right. */
  yaw: number;
  /** Commanded throttle change, in throttle units per second. */
  throttleRate: number;
}

export function createAxisSample(): AxisSample {
  return { roll: 0, pitch: 0, yaw: 0, throttleRate: 0 };
}

/**
 * Look-around command (SPEC §3.3.1). A mouse drag is positional and arrives as an angular delta;
 * a stick is a rate. Both feed the same camera cone, so both are carried rather than forcing one
 * into the other's shape — converting a drag into a rate makes the mouse feel like treacle.
 */
export interface LookCommand {
  /** Stick rate, −1..1. */
  rateX: number;
  rateY: number;
  /** Mouse drag this sample, radians. */
  deltaXRad: number;
  deltaYRad: number;
  /** True while the player is actively looking; when false the view snaps back. */
  active: boolean;
}

export function createLookCommand(): LookCommand {
  return { rateX: 0, rateY: 0, deltaXRad: 0, deltaYRad: 0, active: false };
}

export class KeyboardMouse {
  readonly bindings: Bindings;

  /** Physical key codes currently held. */
  private readonly pressed = new Set<string>();

  /** Actions whose key went down since the last drain. */
  private readonly pendingActions = new Set<InputActionId>();

  // Ramped keyboard axes.
  private keyRoll = 0;
  private keyPitch = 0;
  private keyYaw = 0;

  // Virtual joystick position, −1..1.
  private stickX = 0;
  private stickY = 0;

  /** Mouse movement accumulated since the last sample, in pixels. */
  private pendingMouseX = 0;
  private pendingMouseY = 0;
  private mouseMovedSinceSample = false;

  /** Right-drag look, accumulated in pixels since the last sample. */
  private pendingLookX = 0;
  private pendingLookY = 0;
  private lookButtonDown = false;

  private attached = false;
  private target: HTMLElement | null = null;

  constructor(bindings: Bindings) {
    this.bindings = bindings;
  }

  get pointerLocked(): boolean {
    return typeof document !== 'undefined' && document.pointerLockElement === this.target;
  }

  /** Current virtual stick deflection, for the M2 HUD and for tests. */
  get virtualStick(): { x: number; y: number } {
    return { x: this.stickX, y: this.stickY };
  }

  /** Read-only view of the physical keys currently held. */
  get heldKeys(): ReadonlySet<string> {
    return this.pressed;
  }

  attach(target: HTMLElement): void {
    if (this.attached) return;
    this.attached = true;
    this.target = target;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    target.addEventListener('mousemove', this.onMouseMove);
    target.addEventListener('mousedown', this.onMouseDown);
    target.addEventListener('mouseup', this.onMouseUp);
    target.addEventListener('contextmenu', this.onContextMenu);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.target?.removeEventListener('mousemove', this.onMouseMove);
    this.target?.removeEventListener('mousedown', this.onMouseDown);
    this.target?.removeEventListener('mouseup', this.onMouseUp);
    this.target?.removeEventListener('contextmenu', this.onContextMenu);
    this.target = null;
  }

  /** Press a physical key. Public so tests can drive the device without a DOM. */
  handleKeyDown(code: string, repeat = false): void {
    if (repeat) return;
    this.pressed.add(code);
    for (const action of this.bindings.actionsForKey(code)) {
      this.pendingActions.add(action);
    }
  }

  handleKeyUp(code: string): void {
    this.pressed.delete(code);
  }

  /** Accumulate pointer movement, in pixels. Routed to look while the look button is held. */
  handleMouseMove(movementXPx: number, movementYPx: number): void {
    if (this.lookButtonDown) {
      this.pendingLookX += movementXPx;
      this.pendingLookY += movementYPx;
      return;
    }
    this.pendingMouseX += movementXPx;
    this.pendingMouseY += movementYPx;
    this.mouseMovedSinceSample = true;
  }

  /** Right mouse button: hold to look around, release to snap back. */
  setLookButton(down: boolean): void {
    this.lookButtonDown = down;
    if (!down) {
      this.pendingLookX = 0;
      this.pendingLookY = 0;
    }
  }

  get lookHeld(): boolean {
    return this.lookButtonDown;
  }

  /** Drain the accumulated look drag into a command, in radians. */
  sampleLook(out: LookCommand): LookCommand {
    out.rateX = 0;
    out.rateY = 0;
    out.deltaXRad = this.pendingLookX * CAMERA.lookRateRadPerPixel;
    // Screen y grows downward; dragging down should look down.
    out.deltaYRad = -this.pendingLookY * CAMERA.lookRateRadPerPixel;
    out.active = this.lookButtonDown;
    this.pendingLookX = 0;
    this.pendingLookY = 0;
    return out;
  }

  /** Release every held key — used on window blur, where key-up events never arrive. */
  releaseAll(): void {
    this.pressed.clear();
  }

  /** True if a discrete action fired since the last call, clearing it. */
  consumeAction(action: InputActionId): boolean {
    return this.pendingActions.delete(action);
  }

  clearPendingActions(): void {
    this.pendingActions.clear();
  }

  /** Advance the ramps and the virtual stick by dtS, writing the result into `out`. */
  sample(dtS: number, out: AxisSample): AxisSample {
    const bindings = this.bindings;
    const pressed = this.pressed;

    this.keyRoll = rampAxis(
      this.keyRoll,
      axisFromKeys(bindings, pressed, InputAction.RollRight, InputAction.RollLeft),
      dtS,
    );
    this.keyPitch = rampAxis(
      this.keyPitch,
      axisFromKeys(bindings, pressed, InputAction.PitchUp, InputAction.PitchDown),
      dtS,
    );
    this.keyYaw = rampAxis(
      this.keyYaw,
      axisFromKeys(bindings, pressed, InputAction.YawRight, InputAction.YawLeft),
      dtS,
    );

    this.updateVirtualStick(dtS);

    const options = bindings.options;
    const mouseRoll = options.mouseEnabled ? this.stickX : 0;
    const mouseUp = options.mouseEnabled ? -this.stickY : 0;
    const pitchSign = options.invertPitch ? -1 : 1;

    out.roll = clamp(this.keyRoll + mouseRoll, -1, 1);
    out.pitch = clamp((this.keyPitch + mouseUp) * pitchSign, -1, 1);
    out.yaw = clamp(this.keyYaw, -1, 1);

    let throttleRate = 0;
    if (bindings.isActionPressed(InputAction.ThrottleUp, pressed)) throttleRate += 1;
    if (bindings.isActionPressed(InputAction.ThrottleDown, pressed)) throttleRate -= 1;
    out.throttleRate = throttleRate * INPUT.throttleRatePerS;

    return out;
  }

  /**
   * Move the virtual stick by the accumulated pointer movement, then bleed it back toward
   * centre. The recentring is what makes the model flyable: without it the stick would hold a
   * deflection forever and the aircraft would never return to neutral.
   */
  private updateVirtualStick(dtS: number): void {
    const sensitivity = INPUT.mouseSensitivity * this.bindings.options.mouseSensitivity;
    this.stickX = clamp(
      this.stickX + this.pendingMouseX * sensitivity,
      -INPUT.mouseMaxDeflection,
      INPUT.mouseMaxDeflection,
    );
    this.stickY = clamp(
      this.stickY + this.pendingMouseY * sensitivity,
      -INPUT.mouseMaxDeflection,
      INPUT.mouseMaxDeflection,
    );
    this.pendingMouseX = 0;
    this.pendingMouseY = 0;

    if (!this.mouseMovedSinceSample) {
      const decay = Math.exp(-INPUT.mouseRecentrePerS * dtS);
      this.stickX *= decay;
      this.stickY *= decay;
      if (Math.abs(this.stickX) < INPUT.mouseDeadzone * 0.1) this.stickX = 0;
      if (Math.abs(this.stickY) < INPUT.mouseDeadzone * 0.1) this.stickY = 0;
    }
    this.mouseMovedSinceSample = false;
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    this.handleKeyDown(event.code, event.repeat);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.handleKeyUp(event.code);
  };

  private readonly onBlur = (): void => {
    this.releaseAll();
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.bindings.options.mouseEnabled) return;
    // Look-around is a drag, so it is always movement-based — including when the pointer is not
    // captured. Falling through to the positional branch here silently disabled right-drag look
    // for anyone who had not clicked to lock first.
    if (this.lookButtonDown || this.pointerLocked) {
      this.handleMouseMove(event.movementX, event.movementY);
      return;
    }
    // Unlocked fallback: treat displacement from the centre of the viewport as a deflection,
    // with a dead centre region so the aircraft is controllable without capturing the pointer.
    const halfWidth = window.innerWidth * 0.5;
    const halfHeight = window.innerHeight * 0.5;
    const offsetX = deadzone((event.clientX - halfWidth) / halfWidth, INPUT.mouseDeadzone);
    const offsetY = deadzone((event.clientY - halfHeight) / halfHeight, INPUT.mouseDeadzone);
    this.stickX = clamp(offsetX, -1, 1);
    this.stickY = clamp(offsetY, -1, 1);
    this.mouseMovedSinceSample = true;
  };

  private readonly onMouseDown = (event: MouseEvent): void => {
    if (!this.bindings.options.mouseEnabled) return;
    if (event.button === 2) {
      this.setLookButton(true);
      event.preventDefault();
      return;
    }
    this.target?.requestPointerLock?.();
  };

  private readonly onMouseUp = (event: MouseEvent): void => {
    if (event.button === 2) this.setLookButton(false);
  };

  /** The right button is the look control, so its menu must not open on top of the flight. */
  private readonly onContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };
}

/** +1 for the positive action, −1 for the negative, 0 for neither or both. */
function axisFromKeys(
  bindings: Bindings,
  pressed: ReadonlySet<string>,
  positive: InputActionId,
  negative: InputActionId,
): number {
  const up = bindings.isActionPressed(positive, pressed) ? 1 : 0;
  const down = bindings.isActionPressed(negative, pressed) ? 1 : 0;
  return up - down;
}

/** Move an axis toward its target, faster on release than on press. */
function rampAxis(current: number, target: number, dtS: number): number {
  const rate = target === 0 ? INPUT.keyReturnPerS : INPUT.keyRampPerS;
  const delta = target - current;
  const step = rate * dtS;
  if (Math.abs(delta) <= step) return target;
  return current + Math.sign(delta) * step;
}

/** Rescale outside a dead centre region so the axis starts from zero at the deadzone edge. */
export function deadzone(value: number, threshold: number): number {
  const magnitude = Math.abs(value);
  if (magnitude <= threshold) return 0;
  const scaled = (magnitude - threshold) / (1 - threshold);
  return Math.sign(value) * scaled;
}
